import React, { useEffect } from 'react';
import { Check } from 'lucide-react';
import { formatCHF } from '@/utils/format';

/**
 * PaymentSuccessDialog – hübsche Bestätigung nach erfolgreicher Zahlung.
 * Animiertes Häkchen + Rechnungsdetails. Wiederverwendbar (Belege, Offene Positionen).
 */
const PaymentSuccessDialog = ({ open, onClose, invoiceNumber, amount, studioName, method }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="payment-success-dialog"
    >
      <style>{`
        @keyframes sf-pop{0%{transform:scale(0);opacity:0}60%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
        @keyframes sf-ring{0%{transform:scale(.5);opacity:.7}100%{transform:scale(1.6);opacity:0}}
        @keyframes sf-slideup{0%{transform:translateY(12px);opacity:0}100%{transform:translateY(0);opacity:1}}
      `}</style>
      <div
        className="w-full max-w-sm rounded-2xl p-6 text-center"
        style={{ backgroundColor: '#141310', border: '1px solid rgba(255,255,255,0.1)', animation: 'sf-slideup 0.28s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative mx-auto mb-4" style={{ width: 76, height: 76 }}>
          <span
            className="absolute inset-0 rounded-full"
            style={{ background: 'rgba(34,197,94,0.35)', animation: 'sf-ring 0.9s ease-out' }}
          />
          <div
            className="relative flex items-center justify-center rounded-full"
            style={{
              width: 76, height: 76,
              background: 'rgba(34,197,94,0.15)',
              border: '2px solid #4ade80',
              animation: 'sf-pop 0.45s ease-out',
            }}
          >
            <Check size={40} strokeWidth={3} style={{ color: '#4ade80' }} />
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-1">Zahlung erfolgreich</h3>
        <p className="text-sm text-zinc-400 mb-4">Deine Rechnung wurde bezahlt. Vielen Dank!</p>

        {(invoiceNumber || amount != null || studioName || method) && (
          <div className="rounded-xl p-3 text-left text-sm space-y-1.5 mb-5" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            {invoiceNumber && (
              <div className="flex justify-between gap-3">
                <span className="text-zinc-400">Rechnung</span>
                <span className="text-white font-medium truncate">{invoiceNumber}</span>
              </div>
            )}
            {amount != null && (
              <div className="flex justify-between gap-3">
                <span className="text-zinc-400">Betrag</span>
                <span className="text-white font-semibold">CHF {formatCHF(amount)}</span>
              </div>
            )}
            {studioName && (
              <div className="flex justify-between gap-3">
                <span className="text-zinc-400">Studio</span>
                <span className="text-white">{studioName}</span>
              </div>
            )}
            {method && (
              <div className="flex justify-between gap-3">
                <span className="text-zinc-400">Methode</span>
                <span className="text-white">{method}</span>
              </div>
            )}
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-xl font-medium transition-all hover:opacity-90"
          style={{ backgroundColor: '#c9a34e', color: '#000' }}
          data-testid="payment-success-close"
        >
          Fertig
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccessDialog;
